<template>
    <div class="banner-container" :class="[
        containerClass,
        withBackground ? 'banner-with-bg' : ''
    ]">
        <!-- Background provided via prop -->
        <div v-if="withBackground" class="banner-bg-layer" :style="{
            background: `linear-gradient(to right, ${bg}, color-mix(in srgb, ${bg} 80%, white))`
        }">
            <div class="banner-circle-top" />
            <div class="banner-circle-bottom" />
        </div>

        <!-- Texture Overlay -->
        <div v-if="withBackground && !bgImage" class="banner-texture" :style="textureStyle" />

        <img v-if="withBackground && bgImage" :src="bgImage" class="banner-bg-image" alt="" />

        <!-- Slide content -->
        <div class="banner-content" :class="innerClass || 'banner-content-default'">
            <!-- Icon -->
            <div v-if="icon" class="banner-icon-wrapper"
                :style="{ backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }">
                <div class="material-icons banner-icon" :style="{ color: textColor }">{{ icon }}</div>
            </div>

            <!-- Text -->
            <div class="banner-text">
                <p class="banner-title" :style="{ color: textColor }">
                    {{ title }}
                </p>
                <p v-if="description" class="banner-description" :style="{ color: textColorMuted }">
                    {{ description }}
                </p>
            </div>

            <!-- CTA link -->
            <a v-if="cta" :href="ctaHref" target="_blank" rel="noopener noreferrer" class="banner-cta" :style="{
                color: isDark ? bg : textColor,
                backgroundColor: isDark ? '#ffffff' : 'rgba(0,0,0,0.1)'
            }">
                {{ cta }}
            </a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Banner',
    props: {
        icon: {
            type: String,
            default: undefined
        },
        title: {
            type: String,
            required: true
        },
        description: {
            type: String,
            default: undefined
        },
        bg: {
            type: String,
            default: ''
        },
        bgImage: {
            type: String,
            default: undefined
        },
        cta: {
            type: String,
            default: undefined
        },
        ctaHref: {
            type: String,
            default: undefined
        },
        ctaStyle: {
            type: String,
            default: undefined
        },
        withBackground: {
            type: Boolean,
            default: true
        },
        containerClass: {
            type: String,
            default: ''
        },
        innerClass: {
            type: String,
            default: ''
        }
    },
    computed: {
        isDark() {
            return this.getLuminance(this.bg) < 0.5;
        },
        textColor() {
            return this.isDark ? '#ffffff' : '#212529';
        },
        textColorMuted() {
            return this.isDark ? 'rgba(255,255,255,0.85)' : 'rgba(33,37,41,0.7)';
        }
    },
    methods: {
        getLuminance(hex) {
            if (!hex || !hex.startsWith('#')) return 0;
            const color = hex.replace('#', '');
            const fullHex = color.length === 3
                ? color.split('').map(c => c + c).join('')
                : color;
            const r = parseInt(fullHex.substr(0, 2), 16) / 255;
            const g = parseInt(fullHex.substr(2, 2), 16) / 255;
            const b = parseInt(fullHex.substr(4, 2), 16) / 255;
            const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
            return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
        }
    },
    data() {
        return {
            textureStyle: {
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23FFFFFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' stroke-opacity='0.15'%3E%3Cpath d='M41 36L32 30.81'/%3E%3Cpath d='M45.5 42.6V34c0-1.66-1.34-3-3-3l-7-4a2 2 0 0 0-2 0l-7 4a2 2 0 0 0-2 2v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1.5-1.13'/%3E%3Cpolyline points='27.77 32.96 36.5 38.01 45.23 32.96'/%3E%3Cline x1='36.5' y1='48.08' x2='36.5' y2='38'/%3E%3C/g%3E%3C/svg%3E")`
            }
        }
    }
}
</script>

<style scoped>
.banner-container {
    position: relative;
    overflow: hidden;
    min-height: 125px;
    display: flex;
    align-items: center;
}

.banner-with-bg {
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -4px rgba(0, 0, 0, 0.1);
}

.banner-circle-top {
    position: absolute;
    right: -32px;
    top: -32px;
    height: 128px;
    width: 128px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.1);
}

.banner-circle-bottom {
    position: absolute;
    bottom: -16px;
    right: -16px;
    height: 96px;
    width: 96px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.05);
}

.banner-bg-layer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: all 0.7s ease;
}

.banner-texture {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0.1;
    mix-blend-mode: overlay;
    pointer-events: none;
}

.banner-bg-image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    mix-blend-mode: soft-light;
    opacity: 0.6;
}

.banner-content {
    position: relative;
    display: flex;
    align-items: center;
    gap: 24px;
}

.banner-content-default {
    padding: 24px 50px;
    height: 100%;
    width: 100%;
}

.banner-icon-wrapper {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.banner-icon {
    font-size: 32px;
    width: 32px;
    height: 32px;
    color: #ffffff;
}

.banner-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.banner-title {
    font-weight: 700;
    font-size: 1.25rem;
    color: #ffffff;
    line-height: 1.25;
    margin: 0;
}

.banner-description {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
    margin: 4px 0 0 0;
}

.banner-cta {
    flex-shrink: 0;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 700;
    transition: all 0.2s ease;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ffffff;
}

@media (max-width: 768px) {
    .banner-content {
        flex-direction: column;
        gap: 10px;
    }

    .banner-icon-wrapper {
        display: none;
    }

    .banner-text {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .banner-title {
        font-weight: 700;
        font-size: 1rem;
        color: #ffffff;
        line-height: 1.25;
        margin: 0;
    }

    .banner-description {
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.9);
        margin: 4px 0 0 0;
    }

    .banner-cta {
        font-size: 0.8rem;
        padding: 8px 16px;
    }
}



.banner-cta:hover {
    scale: 1.05;
    cursor: pointer;
}
</style>
